import {ActivitiesApi, GEEKS_ACTIVITIES_HISTORY_LIMIT} from '@/api';
import {
  ActivityGroup,
  ActivityHistory,
  WaitingActivityRequests,
  RedirectActivityRequestsResponse,
  ResponseToActivityRequestsResponse,
} from '@/models';
import { GrantActivityToMultipleGeeks } from '@/models/admin-panel';
import { RecentActivity } from '@/models/dashboard';
import { GeeksSkillWithActivities } from '@/models/geek-profile';
import { GeekRewardingRequest, GeekRewardingRequestPayload } from '@/models/reward-geek';
import { Gratification } from '@/models/rules';
import { resetModel, setCollapseIfPending } from '@/utils';
import { Module } from 'vuex';
import { Actions } from './actions';
import { Mutations } from './mutations';
import { RootState } from './root.store';
import {initializeNotCollapsedList} from '@/shared';

const activitiesApi = new ActivitiesApi();

export interface ActivitiesState {
  all: ActivityGroup[];
  geeksActivities: GeeksSkillWithActivities[];
  geeksRecentActivities: RecentActivity[];
  requests: GeekRewardingRequest[];
  gratifications: Gratification[];
  activitiesHistory: ActivityHistory[];
  pagination: {
    offset: number,
    limit: number,
    totalCount: number,
  };
  waitingRequests: WaitingActivityRequests[];
}

export const ActivitiesModule: Module<ActivitiesState, RootState> = {
  state: {
    all: [],
    geeksActivities: [],
    geeksRecentActivities: [],
    requests: [],
    gratifications: [],
    activitiesHistory: [],
    pagination: {
      offset: 0,
      limit: 10,
      totalCount: 0,
    },
    waitingRequests: [],
  },

  mutations: {
    [Mutations.activities.setAll](state, activities: ActivityGroup[]) {
      state.all = activities;
    },
    [Mutations.activities.setRequests](state, requests: GeekRewardingRequest[]) {
      state.requests = requests;
    },
    [Mutations.activities.setGratifications](state, gratifications: Gratification[]) {
      state.gratifications = gratifications;
    },
    [Mutations.activities.addRequests](state, requests: GeekRewardingRequest[]) {
      state.requests.push(...requests);
    },
    [Mutations.activities.incrementPagination](state) {
      state.pagination.offset += GEEKS_ACTIVITIES_HISTORY_LIMIT;
    },
    [Mutations.activities.resetPagination](state) {
      state.pagination = resetModel(state.pagination);
    },
    [Mutations.activities.setRequestsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.activities.setGeekActivities](state, activities: GeeksSkillWithActivities[]) {
      state.geeksActivities = activities;
    },
    [Mutations.activities.setRecentActivities](state, activities: any) {
      state.geeksRecentActivities = activities;
    },
    [Mutations.activities.setActivitiesHistory](state, activitiesHistory: ActivityHistory[]) {
      state.activitiesHistory = activitiesHistory;
    },
    [Mutations.activities.addActivitiesHistory](state, activitiesHistory: ActivityHistory[]) {
      state.activitiesHistory.push(...activitiesHistory);
    },
    [Mutations.activities.setWaitingActivityRequests](state, activityWaitingRequest: WaitingActivityRequests[]) {
      state.waitingRequests = activityWaitingRequest;
    },
  },

  actions: {
    async [Actions.activities.fetchAll]({ commit }) {
      const activities = await activitiesApi.fetchAll();
      commit(Mutations.activities.setAll, activities);
    },
    async [Actions.activities.fetchGratifications]({ commit }) {
      const gratifications = await activitiesApi.fetchGratifications();
      commit(Mutations.activities.setGratifications, gratifications);
    },
    async [Actions.activities.postActivityRequest](_, request: GeekRewardingRequestPayload) {
      await activitiesApi.postActivityRequest(request);
    },
    async [Actions.activities.fetchRequests]({ commit }): Promise<void> {
      commit(Mutations.activities.resetPagination);
      const requests = await activitiesApi.fetchRequests();
      initializeNotCollapsedList(requests.data);
      commit(Mutations.activities.setRequests, setCollapseIfPending(requests));
      commit(Mutations.activities.setRequestsCount, requests.totalCount);
    },
    async [Actions.activities.fetchMoreRequests]({ commit }): Promise<void> {
      commit(Mutations.activities.incrementPagination);
      const requests = await activitiesApi.fetchRequests(
        this.state.activities.pagination.offset,
      );
      initializeNotCollapsedList(requests.data);
      commit(Mutations.activities.addRequests, setCollapseIfPending(requests));
      commit(Mutations.activities.setRequestsCount, requests.totalCount);
    },
    async [Actions.activities.fetchGeekActivities]({ commit }, { seasonId, geekId }) {
      const geekActivities = await activitiesApi.fetchGeekActivities(seasonId, geekId);
      commit(Mutations.activities.setGeekActivities, geekActivities);
    },
    async [Actions.activities.fetchRecentActivities]({ commit }, skill): Promise<void> {
      const geekActivities = await activitiesApi.fetchRecentActivities( skill );
      commit(Mutations.activities.setRecentActivities, geekActivities);
    },
    async [Actions.activities.postGeekActivityRequests](_, request: GrantActivityToMultipleGeeks) {
      await activitiesApi.postGeekActivityRequests(request);
    },
    async [Actions.activities.fetchActivitiesHistory]({ commit }, filters) {
      commit(Mutations.activities.resetPagination);
      const response = await activitiesApi.fetchActivitiesHistory(filters);
      initializeNotCollapsedList(response.data);
      commit(Mutations.activities.setActivitiesHistory, response.data);
      commit(Mutations.activities.setRequestsCount, response.totalCount);
    },
    async [Actions.activities.fetchMoreActivitiesHistory]({ commit, state }, filters) {
      commit(Mutations.activities.incrementPagination);
      const response = await activitiesApi.fetchActivitiesHistory(filters, state.pagination.offset);
      initializeNotCollapsedList(response.data);
      commit(Mutations.activities.addActivitiesHistory, response.data);
      commit(Mutations.activities.setRequestsCount, response.totalCount);
    },
    async [Actions.activities.deleteGeeksActivity](_, id: number) {
      await activitiesApi.deleteGeeksActivity(id);
    },

    async [Actions.activities.fetchWaitingActivityRequests]({ commit }, filters) {
      const response = await activitiesApi.fetchWaitingActivityRequests(filters);
      initializeNotCollapsedList(response);
      commit(Mutations.activities.setWaitingActivityRequests, response);
    },

    async [Actions.activities.confirmWaitingActivityRequest](
      _,
      request: ResponseToActivityRequestsResponse,
    ): Promise<void> {
      await activitiesApi.confirmWaitingActivityRequest(request);
    },

    async [Actions.activities.rejectWaitingActivityRequest](
      _,
      request: ResponseToActivityRequestsResponse,
    ): Promise<void> {
      await activitiesApi.rejectWaitingActivityRequest(request);
    },

    async [Actions.activities.redirectWaitingActivityRequest](
      _,
      request: RedirectActivityRequestsResponse,
      ): Promise<void> {
      await activitiesApi.redirectWaitingActivityRequest(request);
    },
  },
};
